import {
  ShieldCheck,
  Cpu,
  Clock,
  Microscope,
  BookOpen,
  FileText,
  Funnel as Filter,
  Database,
  PenTool,
  Mic,
} from 'lucide-react';

export const DELAY_CLASS = {
  0: "delay-0",
  100: "delay-100",
  150: "delay-150",
  200: "delay-200",
  300: "delay-300",
  400: "delay-400",
  450: "delay-450",
  500: "delay-500",
  600: "delay-600",
};

export function delayClass(ms) {
  return DELAY_CLASS[ms] || "delay-0";
}

export const NAV_LINKS = [
  { id: 'tech', label: 'Our Tech' },
  { id: 'team', label: 'Our Team' },
  { id: 'cases', label: 'Case Studies' },
];

export const BRAND_COLORS = [
  { name: 'Logo / brand', hex: '#0052CE', token: 'brand', swatch: 'bg-brand' },
  { name: 'Primary', hex: '#1565C0', token: 'brand-primary', swatch: 'bg-brand-primary' },
  { name: 'Primary dark', hex: '#0D47A1', token: 'brand-dark', swatch: 'bg-brand-dark' },
  { name: 'Secondary / accent', hex: '#42A5F5', token: 'brand-accent', swatch: 'bg-brand-accent' },
];

export const HERO_OUTCOMES = [
  'to peer-reviewed impact.',
  'to submission-ready drafts.',
  'to published evidence.',
  'to first-round acceptance.',
  'to regulatory-grade reports.',
  'to published papers.',
  'to MDR-ready evidence.',
  'to journal-ready drafts.',
];

export const TRUSTED_BRANDS = [
  "MAYO CLINIC",
  "Cleveland Clinic",
  "JOHNS HOPKINS",
  "Novartis",
  "Medtronic",
  "Stryker",
  "Boston Scientific",
  "Johnson & Johnson",
];

export const SYNTHESIS_FEATURES = [
  {
    size: 'large',
    tone: 'blue',
    kicker: '01. Clinical Intellect',
    title: 'Scientific Authorship',
    description: 'MD and PhD-credentialed experts handle complex data interpretation, narrative structuring, and peer-review rebuttals. We focus purely on the science, ensuring your outcomes are defended with unmatched academic rigor.',
    icon: ShieldCheck,
  },
  {
    size: 'compact',
    tone: 'emerald',
    kicker: '02. The Technology',
    title: 'Automated Compliance',
    description: 'Our proprietary engine maps references and enforces exact journal guidelines instantly. Zero formatting errors. Zero administrative delays.',
    icon: Cpu,
  },
  {
    size: 'compact',
    tone: 'slate',
    kicker: '03. The Result',
    title: 'Rapid Submission',
    description: 'By running clinical drafting and technical formatting simultaneously, we deliver submission-ready manuscripts in a fraction of standard timelines.',
    icon: Clock,
    className: 'bg-white/[0.01]',
  },
];

export const AUDIENCES = [
  {
    title: "Clinical Investigators",
    subtitle: "Surgeons & Physicians",
    desc: "Provide your raw outcomes. We deliver a submission-ready manuscript. Build your academic portfolio rapidly without sacrificing OR time.",
    deliverables: ["Retrospective Cohorts", "Case Series", "Surgical Techniques"],
    icon: Microscope,
  },
  {
    title: "Medical Societies",
    subtitle: "Associations & Registries",
    desc: "We act as your dedicated publication arm, processing complex multi-center data into high-impact, globally recognized consensus guidelines.",
    deliverables: ["Consensus Statements", "Registry Reports", "Clinical Guidelines"],
    icon: BookOpen,
  },
  {
    title: "MedTech & Pharma",
    subtitle: "Industry Sponsors",
    desc: "Accelerate market access. We generate peer-reviewed clinical evidence strictly aligned with your regulatory and commercial strategies.",
    deliverables: ["PMCF & CER Data", "Pivotal Trial Results", "HEOR Publications"],
    icon: FileText,
  },
];

export const PROCESS_STEPS = [
  {
    num: "01",
    title: "Data Architecture",
    detail: "We rigorously audit your raw data, define the core clinical narrative, and map your study to high-probability target journals.",
    outputs: ["Data Audit", "Journal Shortlist", "Protocol Framework"],
  },
  {
    num: "02",
    title: "Tech-Enabled Drafting",
    detail: "Our PhD writers construct the science while our software perfectly automates formatting, referencing, and strict guideline adherence.",
    outputs: ["Zero Formatting Errors", "Automated Referencing", "V1 Manuscript"],
  },
  {
    num: "03",
    title: "Frictionless Review",
    detail: "You receive a clean, readable draft. We implement your clinical feedback asynchronously to lock in the science without taking your OR time.",
    outputs: ["SME Review", "Rapid Revision Cycles", "Final Approval"],
  },
  {
    num: "04",
    title: "Submission & Defense",
    detail: "We take full ownership of submission portals and rigorously draft rebuttals to peer-reviewer critiques until your paper achieves acceptance.",
    outputs: ["Portal Management", "Peer-Review Rebuttals", "Final Publication"],
  },
];

export const TECH_MODULES = [
  {
    code: 'SYS.01',
    title: 'Automated PRISMA Screening',
    description: 'Intelligent screening tools search and filter vast literature databases based on your exact inclusion and exclusion criteria. The system automatically structures the data into compliant PRISMA flow methodologies.',
    tags: ['PRISMA Compliant', 'Automated I/E Criteria'],
    icon: Filter,
    iconWrapClass: 'bg-brand/10 border-brand/20',
    iconClass: 'text-brand-accent',
  },
  {
    code: 'SYS.02',
    title: 'Full-Text Endpoint Extraction',
    description: 'Our extraction agents bypass simple abstracts, parsing full-text PDFs to locate, verify, and extract complex primary and secondary clinical endpoints with benchmarked fidelity.',
    tags: ['Full-Text Parsing', 'Data Structuring'],
    icon: Database,
    iconWrapClass: 'bg-brand-accent/10 border-brand-accent/20',
    iconClass: 'text-brand-accent',
  },
  {
    code: 'SYS.03',
    title: 'Referenced Drafting Engine',
    description: 'Synthesizes state-of-the-art literature into rigorous drafts. To guarantee zero hallucinations, every claim is hyperlinked to a direct, highlighted excerpt within the source PDF for immediate human verification.',
    tags: ['Source PDF Highlighting', 'Zero Hallucination UI'],
    icon: PenTool,
    iconWrapClass: 'bg-slate-500/10 border-slate-500/20',
    iconClass: 'text-white',
  },
  {
    code: 'SYS.04',
    title: 'Conversational PROMs & PREMs',
    description: 'Evidence generation requires data. Our autonomous voice agents conduct natural, conversational follow-ups with patients to collect Patient-Reported Outcomes (PROMs) and Experiences (PREMs), injecting structured data directly into your registry.',
    tags: ['Autonomous Patient Call', 'Structured Outcomes'],
    icon: Mic,
    iconWrapClass: 'bg-brand-dark/20 border-brand-dark/30',
    iconClass: 'text-brand-accent',
  },
];

export const DIVISIONS = [
  'Orthopedics & Spine',
  'Cardiovascular & Structural Heart',
  'Oncology & Immunology',
  'Urology & Robotics',
  'Neurology',
  'Medical Devices & PMCF',
];

export const CASES = [
  {
    title: 'Global MedTech PMCF',
    type: 'Clinical Evaluation',
    metric: '14 Days',
    desc: 'Synthesized 5 years of complex post-market follow-up data into a submission-ready manuscript to secure European MDR compliance ahead of a critical deadline.',
  },
  {
    title: 'Multi-Center Orthopedic Registry',
    type: 'Original Research',
    metric: 'First-Round Acceptance',
    desc: 'Processed unstructured outcomes from 3 high-volume centers, resulting in rapid publication in a top-tier surgical journal with zero formatting revisions requested.',
  },
  {
    title: 'Novel Surgical Technique',
    type: 'Case Series',
    metric: '100% Fidelity',
    desc: 'Collaborated with a leading KOL to draft a nuanced technique paper, navigating rigorous peer review with highly precise, evidence-based rebuttals.',
  },
];
